/**
 * Lightweight logger that silences `debug` / `log` / `info` calls in production
 * builds while preserving `warn` / `error` (which are critical for surfacing
 * real problems via the browser/Sentry pipeline).
 *
 * Usage:
 *     import { logger } from '@/lib/logger';
 *     logger.debug('VslGate: anti-pause resume', err);   // dev-only
 *     logger.warn('Form 1 lost shape', payload);          // always
 *     logger.error('Auth refresh failed', err);           // always
 *
 * Why: production bundles previously leaked debug strings via raw `console.*`
 * calls, polluting end-users' DevTools and impacting bundle size (although the
 * statements themselves are tiny, the strings they pass — sometimes large
 * objects — were uselessly serialized at runtime). The 2026-05 code-quality
 * audit flagged ≥61 unsafe console statements; this helper closes that gap.
 */

// We avoid `process.env` shape mismatches between webpack/CRA and Vite by
// reading both. CRA exposes it through DefinePlugin at build time.
const IS_PROD =
  typeof process !== 'undefined' &&
  typeof process.env !== 'undefined' &&
  process.env.NODE_ENV === 'production';

const noop = () => {};

export const logger = {
  /** Dev-only: high-frequency tracing (every state change, every poll). */
  debug: IS_PROD ? noop : console.debug.bind(console),
  /** Dev-only: standard logging, replaces direct console.log usage. */
  log: IS_PROD ? noop : console.log.bind(console),
  /** Dev-only: informational notices. */
  info: IS_PROD ? noop : console.info.bind(console),
  /** Always-on: unexpected condition that may need attention. */
  warn: console.warn.bind(console),
  /** Always-on: actual failure. */
  error: console.error.bind(console),
};

export default logger;
